import { Spacing } from '@persona-id/design-tokens';
import Fuse from 'fuse.js';
import { compact, findIndex } from 'lodash/fp';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable, DropResult, ResponderProvided } from 'react-beautiful-dnd';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import CaseStatusSticker from 'dashboard/components/CaseStatusSticker';
import DraggableListItem from 'dashboard/components/DraggableListItem';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import ReactSelect from 'dashboard/components/ReactSelect';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import SearchInput from 'dashboard/components-lib/SearchInput';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import { reorder } from 'dashboard/lib/dnd/utils';
import {
  CaseState,
  ChecklistItem,
  generateChecklistItemToken,
  VALID_CASE_FIELD_KEY_REGEX,
} from 'dashboard/models/case-template';
import { ModuleChecklist } from 'dashboard/modules/Checklist';
import CaseStatusOption, {
  CaseStatusOptionType,
} from 'dashboard/views/DashboardCaseTemplatesShowView/CaseStatusTable/CaseStatusOption';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Label, Paragraph } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { FieldSchema } from 'lib/fields';
import useToggle from 'lib/hooks/useToggle';
import { useFormSecurely } from 'lib/security/reactHookForm';

const Container = styled('div')`
  padding: ${Spacing[5]};
`;

const SearchContainer = styled.div`
  display: flex;
`;

const StyledSearchInput = styled(SearchInput)`
  width: 100%;
`;

const ItemHeaderContainer = styled.div`
  background-color: ${({ theme }) => theme.color.background.secondary};
  border-radius: ${({ theme }) => theme.element.borderRadius};

  padding: ${Spacing[3]};
`;

const ItemTablePlaceholder = styled.div`
  padding: ${Spacing[2]};
`;

const ItemContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RequirementContainer = styled('div')`
  display: grid;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: 76px auto;

  align-items: center;
`;

const StickerGroup = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing[1]};
`;

export interface ChecklistPanelProps {
  module: ModuleChecklist;
  checklistItems: ChecklistItem[];
  states: CaseState[];
  fieldSchemas: FieldSchema[];
  updateModule: (module: ModuleChecklist) => void;
  addChecklistItem: (item: ChecklistItem) => void;
}

const ChecklistPanel = ({
  module,
  checklistItems: allItems,
  states,
  fieldSchemas,
  updateModule,
  addChecklistItem,
}: ChecklistPanelProps) => {
  const [addItemModal, { enable: showAddItemModal, disable: hideAddItemModal }] = useToggle(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeItems, setActiveItems] = useState<ChecklistItem[]>([]);

  useEffect(() => {
    const initialActiveChecklistItems = compact(
      module.items.map((itemToken) => {
        const itemIndex = allItems.findIndex((item) => itemToken === item.token);

        return itemIndex !== -1 ? allItems[itemIndex] : null;
      }),
    );

    setActiveItems(initialActiveChecklistItems);
  }, []);

  const updateTitle = (event: ChangeEvent<HTMLInputElement>) => {
    updateModule({ ...module, title: event.target.value });
  };

  const wrappedSetActiveItems = (activeItems: ChecklistItem[]) => {
    setActiveItems(activeItems);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    updateModule({ ...module, items: activeItems.map((item) => item.token) });
  };

  const fuse = useMemo(() => {
    const fuseOptions = {
      shouldSort: true,
      threshold: 0.4,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys: [{ name: 'description', weight: 1.0 }],
    };

    return new Fuse(allItems, fuseOptions);
  }, [allItems]);

  const filteredAllItems =
    searchTerm === '' ? allItems : fuse.search(searchTerm).map(({ item }) => item);

  const unselectedItems = useMemo(() => {
    return filteredAllItems.filter(
      (item) => !(activeItems || []).some((i) => i.token === item.token),
    );
  }, [filteredAllItems, activeItems]);

  const filteredActiveItems = useMemo(() => {
    return compact(
      activeItems.map((item) => {
        const itemIdx = findIndex((i) => i.token === item.token, filteredAllItems || []);
        return itemIdx === -1 ? null : filteredAllItems[itemIdx];
      }),
    );
  }, [filteredAllItems, activeItems]);

  const handleDragEnd = (result: DropResult, _provided: ResponderProvided) => {
    const { destination, source } = result;
    if (
      !destination ||
      (destination.droppableId === source.droppableId && destination.index === source.index)
    ) {
      return;
    }

    const reorderedItems = reorder(activeItems, source.index, destination.index);
    wrappedSetActiveItems(reorderedItems);
  };

  // generateItemDnd generates a list of toggleable drag and drop checklist item, given a list of items
  // to generate from and a toggle function which takes in an item and returns a function with no params
  // that toggles the item.
  const generateItemDnds = ({
    items,
    generateToggle,
    isToggled,
    shouldDisableDrag,
  }: {
    items: ChecklistItem[];
    generateToggle: (item: string) => () => void;
    isToggled: boolean;
    shouldDisableDrag: boolean;
  }) => {
    return items.map((item, idx) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const handleToggle = generateToggle(item.token);
      const requiredFor = item['required-for'] != null && item['required-for'].length > 0;

      return (
        <DraggableListItem
          key={item.token}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          draggableId={item.token}
          index={idx}
          disableDrag={shouldDisableDrag}
          dataTest={`checklist-item-editor-table__draggable-item--${item.token}`}
          suffix={<ToggleSlider value={isToggled} onChange={handleToggle} />}
        >
          <ItemContainer>
            <Paragraph>{item.description}</Paragraph>
            {requiredFor && (
              <RequirementContainer>
                <Label size='sm'>Required for:</Label>
                <StickerGroup>
                  {item['required-for'].map((stateId: string) => {
                    const state = states.find(
                      (state: CaseState) => state.token === stateId && !state['archived-at'],
                    );
                    return (
                      <>
                        {state && (
                          <CaseStatusSticker
                            data-test={`case-layout-checklist-module__${item.description}--${stateId}`}
                            key={`${item.description} - ${stateId}`}
                            caseState={state}
                          />
                        )}
                      </>
                    );
                  })}
                </StickerGroup>
              </RequirementContainer>
            )}
          </ItemContainer>
        </DraggableListItem>
      );
    });
  };

  const generateSelectedItemsToggle = (itemToken: string) => () => {
    const updatedItems = activeItems.filter((i) => i.token !== itemToken);
    wrappedSetActiveItems(updatedItems);
  };

  const activeDndItems = generateItemDnds({
    items: filteredActiveItems,
    generateToggle: generateSelectedItemsToggle,
    isToggled: true,
    shouldDisableDrag: false,
  });

  const generateUnselectedItemsToggle = (itemToken: string) => () => {
    const item = allItems.find((i) => i.token === itemToken);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const updatedItems = activeItems.concat(item);
    wrappedSetActiveItems(updatedItems);
  };
  const unselectedDndItems = generateItemDnds({
    items: unselectedItems,
    generateToggle: generateUnselectedItemsToggle,
    isToggled: false,
    shouldDisableDrag: true,
  });

  return (
    <>
      <Container>
        <Heading size='sm'>Settings</Heading>
        <Spacer height={3} />
        <Label size='md' weight='bold' id='checklist-panel__title-input--label'>
          Title
        </Label>
        <Input
          id='checklist-panel__title-input'
          aria-labelledby='checklist-panel__title-input--label'
          type='text'
          onChange={updateTitle}
          value={module.title}
        />

        <Spacer height={3} />

        <Label size='md' weight='bold' id='checklist-panel__search-input--label'>
          Items
        </Label>
        <SearchContainer>
          <StyledSearchInput
            id='checklist-panel__search-input'
            aria-labelledby='checklist-panel__search-input--label'
            title={null}
            value={searchTerm}
            handleInput={setSearchTerm}
            placeholder='Search by item...'
            dataTest='checklist-item-panel-item-table__search'
          />
        </SearchContainer>
        <Spacer height={2} />
        <ItemHeaderContainer>
          <Label size='md' weight='bold'>
            Items
          </Label>
        </ItemHeaderContainer>

        {allItems.length === 0 && (
          <ItemTablePlaceholder>
            <Paragraph>Add checklist items to get started.</Paragraph>
          </ItemTablePlaceholder>
        )}

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId='checklist-item-panel-item-table__droppable-selected'>
            {(provided) => {
              return (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {activeDndItems}
                  {provided.placeholder}
                </div>
              );
            }}
          </Droppable>
        </DragDropContext>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId='checklist-item-panel-item-table__droppable-unselected'>
            {(provided) => {
              return (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {unselectedDndItems}
                  {provided.placeholder}
                </div>
              );
            }}
          </Droppable>
        </DragDropContext>
        <Spacer height={1} />
        <Button
          id='checklist-item-panel__add-item'
          data-test='checklist-item-panel__add-item'
          variant='basic'
          width='auto'
          onClick={showAddItemModal}
        >
          Add item
        </Button>
      </Container>
      <ModalWrapper isOpen={addItemModal} handleCloseModal={hideAddItemModal}>
        <AddItemModalContentReactForm
          states={states}
          fieldSchemas={fieldSchemas}
          initialFieldKey={`checklist_item_${allItems.length + 1}`}
          hideAddItemModal={hideAddItemModal}
          addChecklistItem={addChecklistItem}
        />
      </ModalWrapper>
    </>
  );
};

interface AddChecklistItemFormData {
  'field-key': string;
  description: string;
  'required-for'?: string[];
}

interface AddItemModalContentProps {
  states: CaseState[];
  fieldSchemas: FieldSchema[];
  initialFieldKey: string;
  hideAddItemModal: () => void;
  addChecklistItem: (item: ChecklistItem) => void;
}

const AddItemModalContentReactForm = ({
  states,
  fieldSchemas,
  hideAddItemModal,
  initialFieldKey,
  addChecklistItem,
}: AddItemModalContentProps) => {
  const submit = ({
    'field-key': fieldKey,
    description,
    'required-for': requiredFor,
  }: AddChecklistItemFormData) => {
    const newItem: ChecklistItem = {
      token: generateChecklistItemToken(),
      'field-key': fieldKey,
      description: description,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      'required-for': requiredFor,
    };

    addChecklistItem(newItem);
    hideAddItemModal();
  };

  const stateOptions = states
    .filter((s) => !s['archived-at'])
    .map((state) => {
      return {
        label: state.name,
        value: state.token,
        backgroundColor: state.color,
      };
    });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useFormSecurely({
    defaultValues: {
      description: '',
      'field-key': initialFieldKey,
      'required-for': [],
    },
  });

  return (
    <ModalContainer data-test='checklist-item-add-item__modal'>
      <ModalHeader onClose={hideAddItemModal}>Add checklist item</ModalHeader>
      <ModalBody>
        <form onSubmit={handleSubmit(submit)}>
          <Label size='md' weight='bold' id='checklist-panel-add-item__description--label'>
            Item description
          </Label>
          <Input
            {...register('description', { required: 'Please add a description for your item.' })}
            id='checklist-panel-add-item__description'
            aria-labelledby='checklist-panel-add-item__description--label'
            placeholder='Add a description for your item'
            data-test='checklist-item-add-item__item-description'
          />
          {errors['description'] && <FormError>{errors['description'].message}</FormError>}
          <Spacer height={1} />

          <div>
            <Label size='md' weight='bold' id='checklist-panel-add-item__field-key--label'>
              Item key
            </Label>
            <HelpIcon>
              <Paragraph>
                A unique identifier for your checklist item; used for updating checklist items via
                API.
              </Paragraph>
            </HelpIcon>
          </div>
          <Input
            {...register('field-key', {
              required: 'Please input a key for your item.',
              pattern: {
                value: VALID_CASE_FIELD_KEY_REGEX,
                message: `Field keys should only contain letters or underscores "_".`,
              },
              validate: (fieldKey: string) =>
                !fieldSchemas.map((f) => f.key).includes(fieldKey) ||
                'A field with this key already exists. Please choose a different key.',
            })}
            id='checklist-panel-add-item__field-key'
            aria-labelledby='checklist-panel-add-item__field-key--label'
            placeholder='Add a key for your item'
            data-test='checklist-item-add-item__field-key'
          />
          {errors['field-key'] && <FormError>{errors['field-key'].message}</FormError>}
          <Spacer height={1} />

          <div>
            <Label
              size='md'
              weight='bold'
              id='checklist-panel-add-item__select-required-for-states--label'
            >
              Required for statuses
            </Label>
            <HelpIcon>
              <Paragraph>
                Cases can only transition to these statuses when this checklist item is checked.
              </Paragraph>
            </HelpIcon>
          </div>
          <div data-test='checklist-item-add-item__select-required-for-states'>
            <Controller
              control={control}
              name='required-for'
              render={({ field: { onChange, value } }) => (
                <ReactSelect<string, CaseStatusOptionType, boolean>
                  id='checklist-panel-add-item__select-required-for-states'
                  aria-labelledby='checklist-panel-add-item__select-required-for-states--label'
                  onChange={onChange}
                  value={value}
                  placeholder='Status...'
                  components={{ Option: CaseStatusOption }}
                  options={stateOptions}
                  portal={true}
                  isMulti={true}
                  isClearable={true}
                />
              )}
            />
          </div>
          {errors['required-for'] && <FormError>{errors['required-for'].message}</FormError>}
          <Spacer height={4} />
          <HStack equal={true}>
            <Button
              onClick={hideAddItemModal}
              id='checklist-item-add-item__cancel'
              variant='basic'
              disabled={false}
            >
              Cancel
            </Button>
            <Button type='submit' id='checklist-item-add-item__add-item' variant='primary'>
              Add item
            </Button>
          </HStack>
        </form>
      </ModalBody>
    </ModalContainer>
  );
};

export default ChecklistPanel;
